import React, { useEffect, useMemo, useState } from 'react';
import PendingMatches from './PendingMatches';
import ApprovedMatches from './ApprovedMatches';
import NotPursuedMatches from './NotPursuedMatches';
import { DatePicker } from '../common';
import { InfoTip } from '../common/InfoTip';
import { useMatchCounts } from '../../hooks/useMatch';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { eventService, VALID_GOALS } from '../../services/eventService';
import { MATCH_STATUS } from '../../types/match';
import { Tooltip } from '@radix-ui/themes';
import { InfoCircle } from 'iconoir-react';
import { routes } from '../../lib/routes';
import type { TooltipKey } from '../../tooltips/copy';

const goalLabel = (slug: string) =>
  VALID_GOALS.find((g) => g.value === slug)?.label || slug;
interface NetworkMatchesProps {
  search: string;
  setSearch: (search: string) => void;
  // Multi-event: the event filter is owned by the page so the stats header
  // above this component can rescope with it. null = all events (includes
  // legacy matches that carry no event tag).
  eventId: string | null;
  onEventIdChange: (eventId: string | null) => void;
}

const NetworkMatches = ({ search, setSearch, eventId, onEventIdChange }: NetworkMatchesProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const { data: myEvents } = useQuery({
    queryKey: ['events', 'mine'],
    queryFn: eventService.myEvents,
  });

  // Multi-event: the event currently filtered on (if any), its display name,
  // and the caller's own goals for it. Powers the "your goal here" line and
  // the event-aware empty state, closing the goal -> matches loop in place.
  const selectedEvent = eventId
    ? myEvents?.find((ev) => ev.id === eventId) ?? null
    : null;
  const selectedEventName = selectedEvent?.name ?? 'this event';
  const myGoalsHere = (selectedEvent?.my_goals ?? []).map(goalLabel);
  // Matching window: an organizer can schedule when matching opens for their
  // event. If it has not opened yet, say so explicitly. Without this the user
  // sees an ordinary empty list, which reads as the product being broken --
  // the same perception problem the early-mover nudges exist to fix.
  //
  // Applies to the filtered event, or to the user's only event when no filter
  // is set (the common case: one event, matching not yet open).
  const windowEvent = selectedEvent ?? (myEvents?.length === 1 ? myEvents[0] : null);
  const opensAt =
    windowEvent?.matching_starts_at &&
    new Date(windowEvent.matching_starts_at).getTime() > Date.now()
      ? windowEvent.matching_starts_at
      : null;
  const opensAtLabel = opensAt
    ? new Date(opensAt).toLocaleString('en-US', {
        // Render in the event's own zone so a distant attendee is not told a
        // time that is a day out; label it when we know the zone.
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: windowEvent?.timezone ?? undefined,
        timeZoneName: windowEvent?.timezone ? 'short' : undefined,
      })
    : null;

  // Event-aware empty copy: a fresh joiner lands on an empty filtered list, so
  // reassure rather than the generic "adjust your filters". null -> default.
  // Switched off outright by the organizer. Distinct from "not opened yet":
  // there is no date to promise, so say what is true without inventing one.
  const matchingOff = windowEvent?.matching_enabled === false;
  const emptyMessage = matchingOff
    ? `Matching is not running for ${windowEvent?.name ?? 'this event'} right now. Your profile is saved, and matches will appear here when the organizer turns it back on.`
    : opensAtLabel
    ? `Matching for ${windowEvent?.name ?? 'this event'} opens ${opensAtLabel}. Finish your profile now and your matches will be ready the moment it does.`
    : eventId
      ? `No matches for ${selectedEventName} yet. Your agent surfaces them here as the right people join.`
      : undefined;
  const { data: matchCounts, isLoading, refetch: fetchMatchCounts } = useMatchCounts({
    start_date: startDate?.toISOString(),
    end_date: endDate?.toISOString(),
    // Keeps the tab counts in step with the event-filtered lists below.
    event_id: eventId ?? undefined,
  });

  const tabs = useMemo<
    { label: string; value: string; count: number; tooltipKey: TooltipKey }[]
  >(
    () => [
      {
        label: 'Pending',
        value: MATCH_STATUS.PENDING,
        count: matchCounts?.result?.pending ?? 0,
        tooltipKey: 'matches-tab-pending',
      },
      {
        label: 'Approved',
        value: MATCH_STATUS.APPROVED,
        count: matchCounts?.result?.approved?.total ?? 0,
        tooltipKey: 'matches-tab-approved',
      },
      {
        label: 'Passed',
        value: MATCH_STATUS.NOT_PURSUED,
        count: matchCounts?.result?.passed?.total ?? 0,
        tooltipKey: 'matches-tab-passed',
      },
    ],
    [matchCounts]
  );

  const onDateChange = (dates: Date | [Date | null, Date | null] | null) => {
    if (Array.isArray(dates)) {
      const [start, end] = dates;
      setStartDate(start);
      setEndDate(end);
      setSearch('');
    }
  };

  useEffect(() => {
    fetchMatchCounts();
  }, [endDate]);

  const renderTab = () => {
    switch (activeTab) {
      case MATCH_STATUS.PENDING:
        return (
          <PendingMatches
            onDataChange={() => setSearch('')}
            startDate={startDate}
            endDate={endDate}
            eventId={eventId}
            search={search}
            emptyMessage={emptyMessage}
          />
        );
      case MATCH_STATUS.APPROVED:
        return (
          <ApprovedMatches
            onDataChange={() => setSearch('')}
            startDate={startDate}
            endDate={endDate}
            eventId={eventId}
            search={search}
            counts={matchCounts?.result}
            emptyMessage={emptyMessage}
          />
        );
      case MATCH_STATUS.NOT_PURSUED:
        return (
          <NotPursuedMatches
            onDataChange={() => setSearch('')}
            startDate={startDate}
            endDate={endDate}
            eventId={eventId}
            search={search}
            counts={matchCounts?.result}
            emptyMessage={emptyMessage}
          />
        );
      default:
        return (
          <PendingMatches
            onDataChange={() => setSearch('')}
            startDate={startDate}
            endDate={endDate}
            eventId={eventId}
            search={search}
            emptyMessage={emptyMessage}
          />
        );
    }
  };

  return (
    <div>
      <div className='flex md:flex-row flex-col items-center justify-between mb-6'>
        <div className='flex items-center gap-1 sm:gap-2 order-2 md:order-1 whitespace-nowrap overflow-x-auto scrollbar-hide w-full md:w-fit'>
          {/* Buttons, not clickable divs. These were `<div onClick>`, so the
              primary filter on this screen sat outside the tab order entirely
              and screen readers announced three unlabelled groups. role=tab +
              aria-selected states which one is active; the tooltip span inside
              already stops propagation so it keeps working. */}
          {tabs.map(tab => (
            <button
              type='button'
              role='tab'
              aria-selected={activeTab === tab.value}
              disabled={isLoading}
              className={`py-2 px-4 rounded-lg text-xs font-semibold min-h-10 flex items-center justify-center cursor-pointer duration-200 text-body-700 ${activeTab === tab.value ? 'bg-theme-blue-500' : ''}`}
              key={tab.value}
              onClick={() => {
                if (isLoading) return;
                setActiveTab(tab.value);
              }}
            >
              <span className='leading-[1rem] mx-2'>{tab.label}</span>
              <span
                className={`text-sm text-body-500 min-w-7 h-7 flex items-center justify-center rounded-md bg-white`}
              >
                {tab.count}
              </span>
              {/* Jun-18 (a user V1 test): per-tab info tooltip. stopPropagation
                  so tapping the ⓘ shows the explainer without switching tab. */}
              <span
                className='ml-1.5 inline-flex items-center'
                onClick={e => e.stopPropagation()}
              >
                <InfoTip tooltipKey={tab.tooltipKey} side='bottom'>
                  <InfoCircle className='w-3.5 h-3.5 text-body-400 hover:text-body-600' />
                </InfoTip>
              </span>
            </button>
          ))}
        </div>
        {/* Jun-16 (a user V1 test, D9): "Why are dates here?" — add an info
            tooltip that explains what the date filter actually does.
            Jun-17 (a user round 2):
              R2-2: the tooltip said "lists above" but the lists render BELOW;
                    reworded to plain English with no directional reference.
              R2-1: "Once a date is selected, how do I remove it?" — added
                    isClearable so the range can be cleared in one click.
              Jun-18: reworded shorter / more human per "re-write content to
                    sound more human and to the point" (the old "above" wording
                    was already gone since R2-2; this just tightens it). */}
        <div className='flex items-center gap-1.5 w-full md:w-fit order-1 md:order-2 md:mb-0 mb-5'>
          {(myEvents?.length ?? 0) > 1 && (
            <select
              className='h-10 rounded-lg border border-body-200 bg-white px-3 text-xs font-semibold text-body-700 w-full md:w-fit'
              value={eventId ?? ''}
              onChange={e => onEventIdChange(e.target.value || null)}
              aria-label='Filter matches by event'
            >
              <option value=''>All events</option>
              {myEvents?.map(ev => (
                <option key={ev.id} value={ev.id}>
                  {ev.name}
                </option>
              ))}
            </select>
          )}
          <DatePicker
            wrapperClassName='w-full md:w-fit'
            startDate={startDate}
            endDate={endDate}
            selectsRange
            isClearable
            placeholderText='Select Dates'
            onChange={onDateChange}
          />
          <Tooltip content='Show only matches created in the dates you pick. Leave it empty to see them all.'>
            <span className='inline-flex cursor-help flex-shrink-0'>
              <InfoCircle className='w-4 h-4 text-body-400 hover:text-body-500' />
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Multi-event: show the caller's own goal for the selected event, right
          where their matches for it are. Closes the goal -> matches loop and
          lets them jump to the editor (My events card on the profile). */}
      {eventId && (
        <div className='mb-4 -mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-body-500'>
          <span>
            Your goal for{' '}
            <span className='font-semibold text-body-700'>{selectedEventName}</span>:{' '}
            {myGoalsHere.length > 0
              ? myGoalsHere.join(', ')
              : 'same as your profile objective'}
          </span>
          <button
            type='button'
            onClick={() => navigate(routes.profile)}
            className='underline text-primary-600 hover:text-primary-700'
          >
            Edit
          </button>
        </div>
      )}

      {renderTab()}
    </div>
  );
};

export default NetworkMatches;
